import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import LoadingSpinner from '../../components/LoadingSpinner';

function RegulationsCard() {
  const [regulationsData, setRegulationsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRegulationsKnowledgeBase = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        // Assuming a new endpoint will be created for this specific purpose
        const response = await axios.get(Config.APIBaseURL + '/frameworks/knowledge-base', {
          headers: {
            'access_token': `Bearer ${token}`
          },
          withCredentials: true
        });
        
        setRegulationsData(response.data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching regulations knowledge base:', error);
        setRegulationsData(getMockRegulationsData());
        setIsLoading(false);
        
        // For development/testing: use mock data when endpoint is not yet available
        if (process.env.NODE_ENV === 'development') {
          setRegulationsData(getMockRegulationsData());
          setIsLoading(false);
          setError(null);
        }
      }
    };

    fetchRegulationsKnowledgeBase();
  }, []);

  // Mock data function for development/testing before backend endpoint is ready
  const getMockRegulationsData = () => {
    return [
      {
        id: 5,
        name: 'Reglamento Ciberseguridad INDOTEL',
        jurisdiction: 'Rep. Dominicana',
        jurisdictionCode: 'DO',
        description: 'El Reglamento de Ciberseguridad para la prestación del servicio de acceso al Internet tiene por objeto establecer medidas de alcance general,\
            que servirán de base a las prestadoras de servicios de acceso a internet y prestadoras de \
            infraestructura activa, para garantizar el continuo funcionamiento y seguridad del servicio de \
            acceso a internet, como también asegurar la integridad, disponibilidad y confidencialidad de la \
            información que se transmite, almacena y/o procesa a través y/o por medio de las infraestructuras \
            y/o plataformas pertenecientes, contratadas o asociadas directamente a las prestadoras de \
            servicio de acceso a internet y prestadoras de infraestructuras activas.',
        effectiveDate: '2022-08-02',
        regulator: 'Instituto Dominicano de Telecomunicaciones (INDOTEL)',
        regulatorUrl: 'https://indotel.gob.do/',
        infoUrl: 'https://indotel.gob.do/transparencia/documentos/reglamentos/'
       },
      {
        id: 1,
        name: 'GDPR',
        jurisdiction: 'European Union',
        jurisdictionCode: 'EU',
        description: 'The General Data Protection Regulation is a regulation in EU law on data protection and privacy in the European Union and the European Economic Area.',
        effectiveDate: '2018-05-25',
        regulator: 'European Data Protection Board',
        regulatorUrl: 'https://edpb.europa.eu/',
        infoUrl: 'https://gdpr.eu/'
      },
      {
        id: 2,
        name: 'HIPAA',
        jurisdiction: 'United States',
        jurisdictionCode: 'US',
        description: 'The Health Insurance Portability and Accountability Act sets the standard for protecting sensitive patient data.',
        effectiveDate: '1996-08-21',
        regulator: 'Department of Health and Human Services',
        regulatorUrl: 'https://www.hhs.gov/',
        infoUrl: 'https://www.hhs.gov/hipaa/'
      },
      {
        id: 3,
        name: 'PCI DSS',
        jurisdiction: 'Global',
        jurisdictionCode: 'GLOBAL',
        description: 'The Payment Card Industry Data Security Standard is an information security standard for organizations that handle credit cards.',
        effectiveDate: '2004-12-15',
        regulator: 'PCI Security Standards Council',
        regulatorUrl: 'https://www.pcisecuritystandards.org/',
        infoUrl: 'https://www.pcisecuritystandards.org/pci_security/'
      },
      {
        id: 4,
        name: 'DORA',
        jurisdiction: 'European Union',
        jurisdictionCode: 'EU',
        description: 'Digital Operational Resilience Act aims to ensure that financial entities can withstand, respond to and recover from ICT-related disruptions and threats.',
        effectiveDate: '2023-01-16',
        regulator: 'European Banking Authority',
        regulatorUrl: 'https://www.eba.europa.eu/',
        infoUrl: 'https://finance.ec.europa.eu/regulation-and-supervision/regulatory-process-financial-services/regulatory-process-financial-services_en'
      }
    ];
  };

  // Function to get country flag
  const getCountryFlag = (countryCode) => {
    if (!countryCode || countryCode === 'GLOBAL') {
      return '🌐'; // Global symbol for worldwide regulations
    }
    
    // Convert country code to uppercase
    const code = countryCode.toUpperCase();
    
    // Country code to regional indicator symbols (flag emoji)
    // Each regional indicator symbol is 127397 more than the corresponding ASCII value
    const codePoints = [...code].map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  return (
    <div className="col-span-full xl:col-span-6 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
      <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center">
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">Compliance Knowledge Base</h2>
      </header>
      <div className="p-3">
        {/* Card content */}
        {isLoading ? (
          <div className="flex justify-center items-center h-80">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-80">
            <p className="text-red-500">{error}</p>
          </div>
        ) : (
          <div className="overflow-y-auto overflow-x-hidden h-80">
            {regulationsData.length > 0 ? (
              <div className="space-y-2">
                {regulationsData.map(regulation => (
                  <div key={regulation.id} className="bg-slate-50 dark:bg-slate-700/30 p-4 rounded-sm border border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center">
                        <h3 className="text-md font-semibold text-slate-800 dark:text-slate-100">{regulation.name}</h3>
                        <div className="ml-2 flex items-center bg-indigo-100 dark:bg-indigo-700/50 text-xs px-2 py-0.5 rounded">
                          <span className="mr-1 text-xl">{getCountryFlag(regulation.jurisdictionCode)}</span>
                          <span className="text-indigo-800 dark:text-indigo-100">{regulation.jurisdiction}</span>
                        </div>
                      </div>
                      {regulation.infoUrl && (
                        <a
                          href={regulation.infoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-500 hover:text-indigo-600"
                          title="More Information"
                        >
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                            <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
                          </svg>
                        </a>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-300 mb-2 line-clamp-3">
                      {regulation.description}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs">
                      <div className="flex items-center text-slate-500 dark:text-slate-400">
                        <svg className="w-3 h-3 fill-current mr-1" viewBox="0 0 16 16">
                          <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 14c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z" />
                          <path d="M8 3.5c-.4 0-.7.3-.7.7v3.9c0 .4.3.7.7.7.4 0 .7-.3.7-.7V4.2c0-.4-.3-.7-.7-.7z" />
                          <path d="M8 9.9c-.4 0-.7.3-.7.7s.3.7.7.7.7-.3.7-.7-.3-.7-.7-.7z" />
                        </svg>
                        <span>Effective: {new Date(regulation.effectiveDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center text-slate-500 dark:text-slate-400">
                        <svg className="w-3 h-3 fill-current mr-1" viewBox="0 0 16 16">
                          <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0.9 13H7.1v-2h1.8v2zm0.1-3.5c0 .4-.3.7-.7.7-.4 0-.7-.3-.7-.7V5.9c0-.4.3-.7.7-.7.4 0 .7.3.7.7v3.6z" />
                        </svg>
                        <span>Regulator:</span>
                        {regulation.regulatorUrl ? (
                          <a 
                            href={regulation.regulatorUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="ml-1 text-indigo-500 hover:text-indigo-600"
                          >
                            {regulation.regulator}
                          </a>
                        ) : (
                          <span className="ml-1">{regulation.regulator}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex justify-center items-center h-full">
                <p className="text-slate-500 dark:text-slate-400">No regulations data available</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RegulationsCard; 